import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import MuiTableRow from '@material-ui/core/TableRow';
import MuiCollapse from '@material-ui/core/Collapse';
import { withStyles } from '@material-ui/core/styles';

import DualActionConfirmationDialog from 'Components/UI/DualActionConfirmationDialog';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import useResource from 'Hooks/useResource';
import EditButton from 'Components/UI/EditButton';
import metaOverrideSetMap from 'Resources';

import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import IconButton from '@material-ui/core/IconButton';
import actions from './actions';

import TableCell from './TableCell';
import { getColumnAlignment } from './utils';

const styles = theme => ({
  root: {},
  rootClickable: {
    '&$hover:hover': {
      cursor: 'pointer',
    },
  },
  rootStriped: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default,
    },
  },
  hover: {},
  cell: {},
  control: {},
  editCell: {
    padding: 0,
  },
});

const createErrorMap = (errors) => {
  const errorMap = {};

  errors.filter(v => v.source).forEach((err) => {
    errorMap[err.source] = err;
  });

  return errorMap;
};

function TableRow({
  actions: actionsProp,
  className,
  classes,
  columns,
  inlineEditable,
  onClick,
  onSubmit,
  resource: resourceFromTableCollection,
  onEditButtonClick,
  CellProps,
  RowProps,
  onExpanded,
  ...rest
}) {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [expandableRowContent, setExpandableRowContent] = useState(null);
  const [confirmDialogOptions, setConfirmDialogOptions] = useState(null);

  /**
   * Resolve classes based on the information of the resource.
   *
   * @callback rowClassesResolver
   * @param {Object} resource FR Resource
   * @return {Any} mui styles or classNames object or null
   */

  /**
   * @property {rowClassesResolver | undefined} RowProps.classesResolver
   */
  const {
    metaOverrideSetName,
    metaOverrideSet,
    stripedRows = false,
    classesResolver,
    ...restRowProps
  } = RowProps;

  const {
    resource: resourceFromUseResource,
    setAttribute,
    setResourceDeprecated,
  } = useResource(resourceFromTableCollection, {
    metaOverrideSet: metaOverrideSetMap[metaOverrideSetName],
  });

  const rowResource = (inlineEditable) ? resourceFromUseResource : resourceFromTableCollection;

  useEffect(() => {
    if (onExpanded) {
      setExpanded(false);
    }
  }, [rowResource]);

  const errorMap = createErrorMap(errors);

  const rootClass = classNames(classes.root, className, {
    [classes.rootClickable]: !!onClick,
    [classes.rootStriped]: stripedRows,
  },
  classesResolver && classesResolver(resourceFromTableCollection));

  // NOTE: This only works for picklists at the moment (only production use case is Cash Flow)
  //       We need to make it so that the "value" parameter here is always the value of the control
  //       rather than something determined by the input (with MUI it is the event which we need to pull
  //       the value off of by doing event.target.value
  const handleSubmit = async (attrName, value) => {
    const originalValue = rowResource.attr(attrName);
    setSaving(true);
    let updatedResource = (attrName) ? rowResource.copy().attr(attrName, value) : rowResource;

    setResourceDeprecated(updatedResource);
    setErrors([]);

    let coll;

    try {
      coll = await updatedResource.submit();

      // Set meta on the new item
      updatedResource = coll.resources().head();
      updatedResource.meta().merge(rowResource.meta());

      setResourceDeprecated(updatedResource);
      setSaving(false);
    } catch (err) {
      const revertedResource = rowResource.copy().attr(attrName, originalValue);
      setResourceDeprecated(revertedResource);

      if (err.errors) {
        setErrors(err.errors);
        const nonSourceErrors = err.errors.filter(v => !v.source).map(v => v.shortDetail || v.detail);
        if (nonSourceErrors.length > 0) {
          displayErrorMessage(err, nonSourceErrors.join(', '));
        }
      } else {
        displayErrorMessage(err, 'Unknown Error Occurred');
      }

      setSaving(false);
      return;
    }

    // onSubmit should handle its own errors
    if (onSubmit) {
      onSubmit(coll);
    }
  };

  const expandableStyle = onExpanded ? { borderBottom: '0' } : {};

  const handleChange = (attrName, value) => {
    setAttribute(attrName, value);
  };

  const handleExpand = () => {
    const content = onExpanded(rowResource);

    // If not expanded, means that we will be expanding
    if (!expanded) {
      setExpandableRowContent(content);
    }

    setExpanded(!expanded);
  };

  return (
    <React.Fragment>
      <MuiTableRow
        classes={{
          root: rootClass,
          hover: classes.hover,
        }}
        hover={!!onClick}
        onClick={onClick}
        {...rest}
        {...restRowProps}
      >
        {onEditButtonClick && (
          <TableCell
            align="center"
            className={classNames(classes.cell, classes.editCell)}
            key="edit"
            {...CellProps}
            style={{
              ...expandableStyle,
              ...CellProps.style,
            }}
          >
            <EditButton onClick={onEditButtonClick} />
          </TableCell>
        )}
        {onExpanded && (
          <TableCell
            align="center"
            className={classNames(classes.cell, classes.editCell)}
            key="collapse"
            {...CellProps}
            style={{
              ...expandableStyle,
              ...CellProps.style,
            }}
          >
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleExpand()}
              variant="icon"
              className={classes.overflowButton}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </TableCell>
        )}
        {/* eslint-disable-next-line arrow-body-style */}
        {columns.map((column) => {
          const columnError = errorMap[column.name];
          const isAction = !!column.action;
          const hasSecondaryConfirmationAction = !!column.secondaryConfirmationAction;

          let action = {};
          let secondaryConfirmationAction = null;
          let ActionComponent = null;
          const metaField = (isAction) ? null : rowResource.meta().field(column.name);

          if (isAction) {
            const actionName = column.action && column.action.name;

            if (!(actionName in actions) && !(actionName in actionsProp)) {
              throw new Error(`Action ${actionName} does not exist`);
            }
            const actn = actionsProp[actionName] || actions[actionName];
            action = {
              ...actn,
              ...column.action,
            };

            if (action.component) {
              ActionComponent = action.component;
            } else {
              throw new Error('Actions must have components');
            }

            if (!action.onSubmit) {
              throw new Error('Actions must have an onSubmit');
            }

            if (hasSecondaryConfirmationAction) {
              const secondaryConfirmationActionName = column.secondaryConfirmationAction && column.secondaryConfirmationAction.name;
              if (!(secondaryConfirmationActionName in actions)) {
                throw new Error(`Secondary Confirmation Action ${secondaryConfirmationActionName} does not exist`);
              }

              secondaryConfirmationAction = {
                ...actions[secondaryConfirmationActionName],
                ...column.secondaryConfirmationAction,
              };

              if (!secondaryConfirmationAction.onSubmit) {
                throw new Error('Actions must have an onSubmit');
              }
            }
          }

          const renderCell = () => {
            if (isAction) {
              return (
                <ActionComponent
                  key={action.columnName}
                  resource={rowResource}
                  onClick={(e) => {
                    if (action.stopPropagation) {
                      e.stopPropagation();
                    }
                    const actionSubmit = () => action.onSubmit(rowResource, action.onSuccess, action.onFailure);
                    const secondaryActionSubmit = secondaryConfirmationAction ? () => secondaryConfirmationAction.onSubmit(rowResource, secondaryConfirmationAction.onSuccess, secondaryConfirmationAction.onFailure) : null;

                    let { confirmSubmit } = column;
                    if (typeof column.confirmSubmit === 'function') {
                      confirmSubmit = column.confirmSubmit(rowResource);
                    }
                    if (confirmSubmit) {
                      setConfirmDialogOptions({
                        onSubmit: actionSubmit,
                        secondaryOnSubmit: secondaryActionSubmit,
                        SecondaryActionComponent: secondaryConfirmationAction ? secondaryConfirmationAction.component : null,
                        ConfirmationDialogProps: column.ConfirmationDialogProps,
                      });
                    } else {
                      actionSubmit();
                    }
                  }}
                />
              );
            }

            return (
              <MetaControl
                key={column.name}
                attrName={column.name}
                className={classNames(classes.control, 'no-helper-text-padding')} // NOTE: Do NOT delete no-helper-text-padding. It does nothing except increase specificity
                helperText="" // Since there is no padding for helper text, don't pass any in
                disabled={saving}
                error={!!columnError}
                metaOverrideSet={metaOverrideSet}
                fullWidth
                onChange={(inlineEditable) ? value => handleChange(column.name, value) : null}
                onInlineSubmit={(inlineEditable) ? (v) => {
                  const submitCell = () => handleSubmit(column.name, v);

                  if (column.confirmSubmit) {
                    setConfirmDialogOptions({
                      onSubmit: submitCell,
                      ConfirmationDialogProps: column.ConfirmationDialogProps,
                    });
                  } else {
                    return submitCell();
                  }
                  return null;
                } : null}
                resource={rowResource}
              />
            );
          };

          return (
            <TableCell
              align={getColumnAlignment(column, metaField)}
              className={classes.cell}
              colSpan={column.span}
              key={(isAction) ? column.action : column.name}
              {...CellProps}
              style={{
                ...expandableStyle,
                ...CellProps.style,
                ...column.style,
              }}
            >
              {renderCell()}
            </TableCell>
          );
        })}
        {confirmDialogOptions && (
          <DualActionConfirmationDialog
            onConfirm={() => {
              setConfirmDialogOptions(null);
              confirmDialogOptions.onSubmit();
            }}
            onCancel={() => setConfirmDialogOptions(null)}
            secondaryOnConfirm={confirmDialogOptions.secondaryOnSubmit ? () => {
              setConfirmDialogOptions(null);
              confirmDialogOptions.secondaryOnSubmit();
            } : null}
            SecondaryActionComponent={confirmDialogOptions.SecondaryActionComponent}
            {...confirmDialogOptions.ConfirmationDialogProps}
          />
        )}
      </MuiTableRow>
      {
        onExpanded && (
        <MuiTableRow>
          <TableCell style={{ padding: 0 }} colSpan={columns?.length + 1}>
            <MuiCollapse in={expanded}>
              {expandableRowContent}
            </MuiCollapse>
          </TableCell>
        </MuiTableRow>
        )
      }
    </React.Fragment>
  );
}

TableRow.defaultProps = {
  className: '',
  actions: {},
  onSubmit: null,
  onClick: () => {},
  CellProps: {},
  RowProps: {},
  editable: null,
  onEditButtonClick: null,
  onExpanded: null,
};

TableRow.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  actions: PropTypes.object,
  resource: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    minWidth: PropTypes.string,
    align: PropTypes.oneOf(['left', 'center', 'right']),
    format: PropTypes.func,
    action: PropTypes.object,
    secondaryConfirmationAction: PropTypes.object,
  })).isRequired,
  inlineEditable: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func,
  onClick: PropTypes.func,
  editable: PropTypes.string,
  onEditButtonClick: PropTypes.func,
  onExpanded: PropTypes.func,
  CellProps: PropTypes.object,
  RowProps: PropTypes.object,
};

export default withStyles(styles)(TableRow);
